import { useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { type GridColDef, useGridApiContext, useGridRootProps } from '@mui/x-data-grid-pro'
import { useMutation } from '@tanstack/react-query'

import { DialogTitleWithCloseButton } from '@carsys/ui'
import { useBlobDownloader } from '@cbgms/base/hooks/use-blob-downloader'
import { useLocalizedCollator } from '@cbgms/base/locale/use-localized-collator'

type SearchPredicate = (column: GridColDef, searchValue: string) => boolean

interface DataGridToolbarExportDialogSlotProps {
  exportCSV: (columns: string[]) => Promise<Blob>
  exportExcel: (columns: string[]) => Promise<Blob>
  filename: string
  searchPredicate?: SearchPredicate
}

declare module '@mui/x-data-grid-pro' {
  interface GridLocaleText {
    toolbarExportTitle: string
    toolbarExportSearchLabel: string
    toolbarExportDeselectAllButton: string
    toolbarExportSelectAllButton: string
    toolbarExportSelectVisibleButton: string
    toolbarExportCancelButton: string
    toolbarExportCSVButton: string
    toolbarExportExcelButton: string
    toolbarExportColumnsToBeExported: (count: number) => string
  }

  interface GridProSlotsComponent {
    ExportDialog: React.JSXElementConstructor<any>
  }

  interface GridProSlotProps {
    exportDialog?: DataGridToolbarExportDialogSlotProps
  }

  interface GridSlotsComponentsProps {
    exportDialog?: DataGridToolbarExportDialogSlotProps
  }
}

export interface DataGridToolbarExportDialogProps extends DataGridToolbarExportDialogSlotProps {
  open: boolean
  onClose: () => void
}

const sortAlphabetically = new Intl.Collator().compare

function defaultSearchPredicate(column: GridColDef, searchValue: string): boolean {
  return (column.headerName || column.field).toLowerCase().indexOf(searchValue) > -1
}

export function DataGridToolbarExportDialog({
  open,
  onClose,
  exportCSV,
  exportExcel,
  filename,
  searchPredicate = defaultSearchPredicate
}: DataGridToolbarExportDialogProps) {
  const apiRef = useGridApiContext()
  const rootProps = useGridRootProps()
  const download = useBlobDownloader()
  const localizedCollator = useLocalizedCollator()

  const allColumns = apiRef.current.getAllColumns()
  const sortedExportableColumns = useMemo(() => {
    return allColumns
      .filter(column => !column.disableExport)
      .sort((a, b) => localizedCollator.compare(a.headerName || a.field, b.headerName || b.field))
  }, [allColumns])

  const visibleColumns = apiRef.current.getVisibleColumns()
  const exportableVisibleColumns = useMemo(() => {
    return visibleColumns.filter(column => !column.disableExport).map(column => column.field)
  }, [visibleColumns])
  const [selectedColumns, setSelectedColumns] = useState(exportableVisibleColumns)

  const [searchValue, setSearchValue] = useState('')

  const filteredColumns = useMemo(() => {
    return sortedExportableColumns.filter(column => searchPredicate(column, searchValue))
  }, [searchPredicate, searchValue, sortedExportableColumns])

  const allColumnsSelected = selectedColumns.length === sortedExportableColumns.length

  const handleToggleSelectAll = () => {
    setSelectedColumns(allColumnsSelected ? [] : sortedExportableColumns.map(column => column.field))
  }

  const handleSelectVisible = () => {
    setSelectedColumns(exportableVisibleColumns)
  }

  const handleToggle = (column: string) => {
    setSelectedColumns(prevState => {
      if (prevState.includes(column)) {
        return prevState.filter(selectedColumn => selectedColumn !== column)
      }

      return prevState.concat(column)
    })
  }

  const isToggled = (column: string) => {
    return selectedColumns.some(visibleColumn => visibleColumn === column)
  }

  const handleSearchValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const csvExport = useMutation(
    (columns: string[]): Promise<Blob> => {
      return exportCSV(columns.slice().sort(sortAlphabetically))
    },
    { onSuccess: blob => download(blob, filename) }
  )

  const excelExport = useMutation(
    (columns: string[]): Promise<Blob> => {
      return exportExcel(columns.slice().sort(sortAlphabetically))
    },
    { onSuccess: blob => download(blob, filename) }
  )

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth='md' scroll='paper'>
      <DialogTitleWithCloseButton onClose={onClose}>{apiRef.current.getLocaleText('toolbarExportTitle')}</DialogTitleWithCloseButton>
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            backgroundColor: theme => theme.palette.background.paper,
            p: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1
          }}
        >
          <rootProps.slots.baseTextField
            label={apiRef.current.getLocaleText('toolbarExportSearchLabel')}
            fullWidth
            onChange={handleSearchValueChange}
            size='small'
            {...rootProps.slotProps?.baseTextField}
          />
          <Typography sx={{ mt: 1 }}>{apiRef.current.getLocaleText('toolbarExportColumnsToBeExported')(selectedColumns.length)}</Typography>
        </Box>
        <List dense disablePadding>
          {filteredColumns.map(column => (
            <ListItemButton key={column.field} onClick={() => handleToggle(column.field)}>
              <rootProps.slots.baseSwitch edge='start' checked={isToggled(column.field)} {...rootProps.slotProps?.baseSwitch} />
              <ListItemText primary={column.headerName} secondary={column.field} />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Stack direction='row' mr='auto' spacing={1}>
          <rootProps.slots.baseButton onClick={handleToggleSelectAll} variant='text' {...rootProps.slotProps?.baseButton}>
            {allColumnsSelected
              ? apiRef.current.getLocaleText('toolbarExportDeselectAllButton')
              : apiRef.current.getLocaleText('toolbarExportSelectAllButton')}
          </rootProps.slots.baseButton>
          <rootProps.slots.baseButton onClick={handleSelectVisible} variant='text' {...rootProps.slotProps?.baseButton}>
            {apiRef.current.getLocaleText('toolbarExportSelectVisibleButton')}
          </rootProps.slots.baseButton>
        </Stack>
        <rootProps.slots.baseButton onClick={onClose} variant='text' {...rootProps.slotProps?.baseButton}>
          {apiRef.current.getLocaleText('toolbarExportCancelButton')}
        </rootProps.slots.baseButton>
        <LoadingButton
          onClick={() => csvExport.mutate(selectedColumns)}
          loading={csvExport.isLoading}
          disabled={selectedColumns.length === 0}
          variant='contained'
          {...rootProps.slotProps?.baseButton}
        >
          {apiRef.current.getLocaleText('toolbarExportCSVButton')}
        </LoadingButton>
        <LoadingButton
          onClick={() => excelExport.mutate(selectedColumns)}
          loading={excelExport.isLoading}
          disabled={selectedColumns.length === 0}
          variant='contained'
          {...rootProps.slotProps?.baseButton}
        >
          {apiRef.current.getLocaleText('toolbarExportExcelButton')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
